/**
 * Element Registry - Schema-driven element definitions for Page Builder
 * 
 * Each block can define its editable elements with:
 * - id: Stable string identifier (e.g., "hero.logo", "hero.tagline")
 * - type: Element type ("text", "image", "button")
 * - label: Human-readable label for inspector
 * - bindPath: Path to value in block.props (e.g., "tagline", "primaryCtaLabel")
 * - stylePath: Path to style tokens in block.props.styles
 * - motionPath: Path to motion tokens in block.props.motion
 * - defaultValue: Fallback value
 * - constraints: Type-specific constraints (minLength, maxLength, etc.)
 */

// Element types
export const ELEMENT_TYPES = {
  TEXT: 'text',
  IMAGE: 'image',
  BUTTON: 'button',
  BADGE: 'badge',
  CARD: 'card', // For items with title + description + icon
};

// Style presets
export const STYLE_PRESETS = {
  textSizes: ['xs', 'sm', 'base', 'lg', 'xl', '2xl', '3xl', '4xl'],
  fontWeights: ['light', 'normal', 'medium', 'semibold', 'bold'],
  alignments: ['left', 'center', 'right'],
  buttonVariants: ['primary', 'secondary', 'outline', 'ghost'],
  paddingSizes: ['sm', 'md', 'lg'],
  radiusSizes: ['none', 'sm', 'md', 'lg', 'full'],
};

// Motion presets
export const MOTION_PRESETS = {
  easings: ['smooth', 'calm', 'snappy'],
  durations: { min: 200, max: 2000, default: 700, step: 50 },
  delays: { min: 0, max: 2000, default: 0, step: 50 },
  fromOpacity: { min: 0, max: 1, default: 0, step: 0.1 },
  fromY: { min: 0, max: 60, default: 30, step: 5 },
  fromScale: { min: 0.8, max: 1, default: 0.95, step: 0.01 },
};

// Default style values
export const DEFAULT_STYLES = {
  text: {
    size: 'base',
    weight: 'normal',
    letterSpacing: 0,
    opacity: 1,
    alignment: 'center',
  },
  image: {
    scale: 1,
    opacity: 1,
    glowStrength: 0,
  },
  button: {
    variant: 'primary',
    padding: 'md',
    radius: 'md',
  },
  badge: {
    size: 'sm',
    opacity: 1,
  },
};

// Default motion values
export const DEFAULT_MOTION = {
  durationMs: 700,
  delayMs: 0,
  easing: 'smooth',
  fromOpacity: 0,
  fromY: 30,
  fromScale: 1,
};

/**
 * Landing Hero Block - Editable Elements Registry
 */
export const LANDING_HERO_ELEMENTS = [
  {
    id: 'hero.logo',
    type: ELEMENT_TYPES.IMAGE,
    label: 'Logo',
    bindPath: 'logoUrl',
    stylePath: 'styles.logo',
    motionPath: 'motion.logo',
    defaultValue: '/logo-large.png',
    constraints: {
      acceptedFormats: ['png', 'svg', 'jpg', 'webp'],
    },
    defaultStyles: {
      scale: 1,
      opacity: 1,
      glowStrength: 0,
    },
    defaultMotion: {
      durationMs: 800,
      delayMs: 0,
      easing: 'smooth',
      fromOpacity: 0,
      fromY: 20,
      fromScale: 0.95,
    },
  },
  {
    id: 'hero.early_access_label',
    type: ELEMENT_TYPES.BADGE,
    label: 'Early Access Label',
    bindPath: 'earlyAccessLabel',
    stylePath: 'styles.earlyAccessLabel',
    motionPath: 'motion.earlyAccessLabel',
    defaultValue: 'Early Access',
    constraints: {
      maxLength: 30,
    },
    defaultStyles: {
      size: 'sm',
      opacity: 1,
    },
    defaultMotion: {
      durationMs: 700,
      delayMs: 150,
      easing: 'smooth',
      fromOpacity: 0,
      fromY: 20,
      fromScale: 1,
    },
  },
  {
    id: 'hero.tagline',
    type: ELEMENT_TYPES.TEXT,
    label: 'Tagline',
    bindPath: 'tagline',
    stylePath: 'styles.tagline',
    motionPath: 'motion.tagline',
    defaultValue: 'Trap Comix costs you nothing to test, but not being discoverable costs you everything.',
    constraints: {
      maxLength: 200,
    },
    defaultStyles: {
      size: '2xl',
      weight: 'light',
      letterSpacing: 0,
      opacity: 0.9,
      alignment: 'center',
    },
    defaultMotion: {
      durationMs: 700,
      delayMs: 300,
      easing: 'smooth',
      fromOpacity: 0,
      fromY: 30,
      fromScale: 1,
    },
  },
  {
    id: 'hero.primary_cta',
    type: ELEMENT_TYPES.BUTTON,
    label: 'Primary CTA',
    bindPath: 'primaryCta',
    stylePath: 'styles.primaryCta',
    motionPath: 'motion.primaryCta',
    defaultValue: {
      label: 'Start Creating',
      destination: '/auth?mode=signup',
    },
    constraints: {
      maxLabelLength: 30,
    },
    defaultStyles: {
      variant: 'primary',
      padding: 'lg',
      radius: 'md',
    },
    defaultMotion: {
      durationMs: 700,
      delayMs: 450,
      easing: 'smooth',
      fromOpacity: 0,
      fromY: 20,
      fromScale: 0.95,
    },
  },
  {
    id: 'hero.secondary_cta',
    type: ELEMENT_TYPES.BUTTON,
    label: 'Secondary CTA',
    bindPath: 'secondaryCta',
    stylePath: 'styles.secondaryCta',
    motionPath: 'motion.secondaryCta',
    defaultValue: {
      label: 'Explore Platform',
      destination: '/',
    },
    constraints: {
      maxLabelLength: 30,
    },
    defaultStyles: {
      variant: 'outline',
      padding: 'lg',
      radius: 'md',
    },
    defaultMotion: {
      durationMs: 700,
      delayMs: 500,
      easing: 'smooth',
      fromOpacity: 0,
      fromY: 20,
      fromScale: 0.95,
    },
  },
];

/**
 * Landing Content Types Block - Editable Elements Registry
 */
export const LANDING_CONTENT_TYPES_ELEMENTS = [
  {
    id: 'content_types.heading',
    type: ELEMENT_TYPES.TEXT,
    label: 'Section Heading',
    bindPath: 'heading',
    stylePath: 'styles.heading',
    motionPath: 'motion.heading',
    defaultValue: 'Your format. Your vision.',
    constraints: { maxLength: 60 },
    defaultStyles: { size: '3xl', weight: 'bold', alignment: 'center', opacity: 1 },
    defaultMotion: { durationMs: 700, delayMs: 0, easing: 'smooth', fromOpacity: 0, fromY: 30, fromScale: 1 },
  },
  {
    id: 'content_types.item_0',
    type: ELEMENT_TYPES.TEXT,
    label: 'Content Type 1',
    bindPath: 'items.0',
    stylePath: 'styles.item_0',
    motionPath: 'motion.item_0',
    defaultValue: { label: 'Comics & Manga', description: 'Multi-page stories with full reader experience', icon: 'BookOpen' },
    defaultStyles: { size: 'xl', weight: 'semibold', opacity: 1 },
    defaultMotion: { durationMs: 700, delayMs: 150, easing: 'smooth', fromOpacity: 0, fromY: 30, fromScale: 1 },
  },
  {
    id: 'content_types.item_1',
    type: ELEMENT_TYPES.TEXT,
    label: 'Content Type 2',
    bindPath: 'items.1',
    stylePath: 'styles.item_1',
    motionPath: 'motion.item_1',
    defaultValue: { label: 'Novels & Fiction', description: 'Chapter-based written content with rich formatting', icon: 'Pen' },
    defaultStyles: { size: 'xl', weight: 'semibold', opacity: 1 },
    defaultMotion: { durationMs: 700, delayMs: 300, easing: 'smooth', fromOpacity: 0, fromY: 30, fromScale: 1 },
  },
  {
    id: 'content_types.item_2',
    type: ELEMENT_TYPES.TEXT,
    label: 'Content Type 3',
    bindPath: 'items.2',
    stylePath: 'styles.item_2',
    motionPath: 'motion.item_2',
    defaultValue: { label: 'Art & Illustration', description: 'Standalone artwork with full gallery features', icon: 'Palette' },
    defaultStyles: { size: 'xl', weight: 'semibold', opacity: 1 },
    defaultMotion: { durationMs: 700, delayMs: 450, easing: 'smooth', fromOpacity: 0, fromY: 30, fromScale: 1 },
  },
];

/**
 * Landing Benefits Block - Editable Elements Registry
 */
export const LANDING_BENEFITS_ELEMENTS = [
  {
    id: 'benefits.heading',
    type: ELEMENT_TYPES.TEXT,
    label: 'Section Heading',
    bindPath: 'heading',
    stylePath: 'styles.heading',
    motionPath: 'motion.heading',
    defaultValue: 'Why build here?',
    constraints: { maxLength: 60 },
    defaultStyles: { size: '3xl', weight: 'bold', alignment: 'center', opacity: 1 },
    defaultMotion: { durationMs: 700, delayMs: 0, easing: 'smooth', fromOpacity: 0, fromY: 30, fromScale: 1 },
  },
  {
    id: 'benefits.item_0',
    type: ELEMENT_TYPES.TEXT,
    label: 'Benefit 1',
    bindPath: 'benefits.0',
    stylePath: 'styles.benefit_0',
    motionPath: 'motion.benefit_0',
    defaultValue: { icon: 'Eye', title: 'Discoverability', description: 'Homepage features, trending sections, and personalized recommendations' },
    defaultStyles: { size: 'lg', weight: 'semibold', opacity: 1 },
    defaultMotion: { durationMs: 700, delayMs: 150, easing: 'smooth', fromOpacity: 0, fromY: 30, fromScale: 1 },
  },
  {
    id: 'benefits.item_1',
    type: ELEMENT_TYPES.TEXT,
    label: 'Benefit 2',
    bindPath: 'benefits.1',
    stylePath: 'styles.benefit_1',
    motionPath: 'motion.benefit_1',
    defaultValue: { icon: 'Shield', title: 'Own your audience', description: 'Direct subscriptions. No algorithm throttling your reach.' },
    defaultStyles: { size: 'lg', weight: 'semibold', opacity: 1 },
    defaultMotion: { durationMs: 700, delayMs: 300, easing: 'smooth', fromOpacity: 0, fromY: 30, fromScale: 1 },
  },
  {
    id: 'benefits.item_2',
    type: ELEMENT_TYPES.TEXT,
    label: 'Benefit 3',
    bindPath: 'benefits.2',
    stylePath: 'styles.benefit_2',
    motionPath: 'motion.benefit_2',
    defaultValue: { icon: 'Zap', title: 'Creator-first tools', description: 'Episode scheduler, analytics, and reader engagement insights' },
    defaultStyles: { size: 'lg', weight: 'semibold', opacity: 1 },
    defaultMotion: { durationMs: 700, delayMs: 450, easing: 'smooth', fromOpacity: 0, fromY: 30, fromScale: 1 },
  },
];

/**
 * Landing Build Phase Block - Editable Elements Registry
 */
export const LANDING_BUILD_PHASE_ELEMENTS = [
  {
    id: 'build_phase.heading',
    type: ELEMENT_TYPES.TEXT,
    label: 'Section Heading',
    bindPath: 'heading',
    stylePath: 'styles.heading',
    motionPath: 'motion.heading',
    defaultValue: 'This is the build phase',
    constraints: { maxLength: 60 },
    defaultStyles: { size: '3xl', weight: 'bold', alignment: 'center', opacity: 1 },
    defaultMotion: { durationMs: 700, delayMs: 0, easing: 'smooth', fromOpacity: 0, fromY: 30, fromScale: 1 },
  },
  {
    id: 'build_phase.subheading',
    type: ELEMENT_TYPES.TEXT,
    label: 'Subheading',
    bindPath: 'subheading',
    stylePath: 'styles.subheading',
    motionPath: 'motion.subheading',
    defaultValue: 'Core features launching now',
    constraints: { maxLength: 100 },
    defaultStyles: { size: 'base', weight: 'normal', alignment: 'center', opacity: 0.7 },
    defaultMotion: { durationMs: 700, delayMs: 100, easing: 'smooth', fromOpacity: 0, fromY: 20, fromScale: 1 },
  },
  {
    id: 'build_phase.step_0',
    type: ELEMENT_TYPES.TEXT,
    label: 'Step 1',
    bindPath: 'steps.0',
    stylePath: 'styles.step_0',
    motionPath: 'motion.step_0',
    defaultValue: 'Episode uploads with page-by-page reader',
    constraints: { maxLength: 80 },
    defaultStyles: { size: 'base', weight: 'normal', opacity: 1 },
    defaultMotion: { durationMs: 700, delayMs: 200, easing: 'smooth', fromOpacity: 0, fromY: 20, fromScale: 1 },
  },
  {
    id: 'build_phase.step_1',
    type: ELEMENT_TYPES.TEXT,
    label: 'Step 2',
    bindPath: 'steps.1',
    stylePath: 'styles.step_1',
    motionPath: 'motion.step_1',
    defaultValue: 'Chapter-based novel publishing',
    constraints: { maxLength: 80 },
    defaultStyles: { size: 'base', weight: 'normal', opacity: 1 },
    defaultMotion: { durationMs: 700, delayMs: 350, easing: 'smooth', fromOpacity: 0, fromY: 20, fromScale: 1 },
  },
  {
    id: 'build_phase.step_2',
    type: ELEMENT_TYPES.TEXT,
    label: 'Step 3',
    bindPath: 'steps.2',
    stylePath: 'styles.step_2',
    motionPath: 'motion.step_2',
    defaultValue: 'Creator profiles with portfolio view',
    constraints: { maxLength: 80 },
    defaultStyles: { size: 'base', weight: 'normal', opacity: 1 },
    defaultMotion: { durationMs: 700, delayMs: 500, easing: 'smooth', fromOpacity: 0, fromY: 20, fromScale: 1 },
  },
];

/**
 * Landing Creators Block - Editable Elements Registry
 */
export const LANDING_CREATORS_ELEMENTS = [
  {
    id: 'creators.heading',
    type: ELEMENT_TYPES.TEXT,
    label: 'Section Heading',
    bindPath: 'heading',
    stylePath: 'styles.heading',
    motionPath: 'motion.heading',
    defaultValue: 'Join the movement',
    constraints: { maxLength: 60 },
    defaultStyles: { size: '3xl', weight: 'bold', alignment: 'center', opacity: 1 },
    defaultMotion: { durationMs: 700, delayMs: 0, easing: 'smooth', fromOpacity: 0, fromY: 30, fromScale: 1 },
  },
  {
    id: 'creators.subheading',
    type: ELEMENT_TYPES.TEXT,
    label: 'Subheading',
    bindPath: 'subheading',
    stylePath: 'styles.subheading',
    motionPath: 'motion.subheading',
    defaultValue: 'Trap Comix is built by and for creators who\'ve been underserved by mainstream platforms.',
    constraints: { maxLength: 200 },
    defaultStyles: { size: 'base', weight: 'normal', alignment: 'center', opacity: 0.7 },
    defaultMotion: { durationMs: 700, delayMs: 100, easing: 'smooth', fromOpacity: 0, fromY: 20, fromScale: 1 },
  },
  {
    id: 'creators.cta',
    type: ELEMENT_TYPES.BUTTON,
    label: 'CTA Button',
    bindPath: 'cta',
    stylePath: 'styles.cta',
    motionPath: 'motion.cta',
    defaultValue: { label: 'Start Creating', destination: '/auth?mode=signup' },
    constraints: { maxLabelLength: 30 },
    defaultStyles: { variant: 'primary', padding: 'lg', radius: 'md' },
    defaultMotion: { durationMs: 700, delayMs: 300, easing: 'smooth', fromOpacity: 0, fromY: 20, fromScale: 0.95 },
  },
];

/**
 * Landing Transparency Block - Editable Elements Registry
 */
export const LANDING_TRANSPARENCY_ELEMENTS = [
  {
    id: 'transparency.heading',
    type: ELEMENT_TYPES.TEXT,
    label: 'Section Heading',
    bindPath: 'heading',
    stylePath: 'styles.heading',
    motionPath: 'motion.heading',
    defaultValue: 'What "Early Access" Means',
    constraints: { maxLength: 60 },
    defaultStyles: { size: 'xl', weight: 'semibold', alignment: 'center', opacity: 1 },
    defaultMotion: { durationMs: 700, delayMs: 0, easing: 'smooth', fromOpacity: 0, fromY: 30, fromScale: 1 },
  },
  {
    id: 'transparency.item_0',
    type: ELEMENT_TYPES.TEXT,
    label: 'Item 1',
    bindPath: 'items.0',
    stylePath: 'styles.item_0',
    motionPath: 'motion.item_0',
    defaultValue: 'Some features are still being refined',
    constraints: { maxLength: 100 },
    defaultStyles: { size: 'base', weight: 'normal', opacity: 0.7 },
    defaultMotion: { durationMs: 700, delayMs: 150, easing: 'smooth', fromOpacity: 0, fromY: 20, fromScale: 1 },
  },
  {
    id: 'transparency.item_1',
    type: ELEMENT_TYPES.TEXT,
    label: 'Item 2',
    bindPath: 'items.1',
    stylePath: 'styles.item_1',
    motionPath: 'motion.item_1',
    defaultValue: 'You may see placeholder or test accounts',
    constraints: { maxLength: 100 },
    defaultStyles: { size: 'base', weight: 'normal', opacity: 0.7 },
    defaultMotion: { durationMs: 700, delayMs: 250, easing: 'smooth', fromOpacity: 0, fromY: 20, fromScale: 1 },
  },
  {
    id: 'transparency.item_2',
    type: ELEMENT_TYPES.TEXT,
    label: 'Item 3',
    bindPath: 'items.2',
    stylePath: 'styles.item_2',
    motionPath: 'motion.item_2',
    defaultValue: 'The platform will visibly evolve',
    constraints: { maxLength: 100 },
    defaultStyles: { size: 'base', weight: 'normal', opacity: 0.7 },
    defaultMotion: { durationMs: 700, delayMs: 350, easing: 'smooth', fromOpacity: 0, fromY: 20, fromScale: 1 },
  },
  {
    id: 'transparency.footer',
    type: ELEMENT_TYPES.TEXT,
    label: 'Footer Text',
    bindPath: 'footer',
    stylePath: 'styles.footer',
    motionPath: 'motion.footer',
    defaultValue: 'You\'re helping us build this the right way.',
    constraints: { maxLength: 100 },
    defaultStyles: { size: 'sm', weight: 'normal', alignment: 'center', opacity: 0.5 },
    defaultMotion: { durationMs: 700, delayMs: 500, easing: 'smooth', fromOpacity: 0, fromY: 20, fromScale: 1 },
  },
];

/**
 * Block to Elements mapping
 * Add new blocks here as they adopt element-level editing
 */
export const BLOCK_ELEMENTS_REGISTRY = {
  landing_hero: LANDING_HERO_ELEMENTS,
  landing_content_types: LANDING_CONTENT_TYPES_ELEMENTS,
  landing_benefits: LANDING_BENEFITS_ELEMENTS,
  landing_build_phase: LANDING_BUILD_PHASE_ELEMENTS,
  landing_creators: LANDING_CREATORS_ELEMENTS,
  landing_transparency: LANDING_TRANSPARENCY_ELEMENTS,
};

/**
 * Get editable elements for a block type
 */
export const getBlockElements = (blockType) => {
  return BLOCK_ELEMENTS_REGISTRY[blockType] || [];
};

/**
 * Get element schema by ID
 */
export const getElementById = (blockType, elementId) => {
  const elements = getBlockElements(blockType);
  return elements.find(el => el.id === elementId);
};

/**
 * Get value from block props using bindPath
 */
export const getElementValue = (blockProps, element) => {
  const path = element.bindPath;
  const parts = path.split('.');
  let value = blockProps;
  
  for (const part of parts) {
    if (value === undefined || value === null) {
      return element.defaultValue;
    }
    value = value[part];
  }
  
  return value !== undefined ? value : element.defaultValue;
};

/**
 * Set value in block props using bindPath
 */
export const setElementValue = (blockProps, element, newValue) => {
  const path = element.bindPath;
  const parts = path.split('.');
  const result = { ...blockProps };
  
  let current = result;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    current[part] = current[part] ? { ...current[part] } : {};
    current = current[part];
  }
  
  current[parts[parts.length - 1]] = newValue;
  return result;
};

/**
 * Get styles from block props using stylePath
 */
export const getElementStyles = (blockProps, element) => {
  if (!element.stylePath) return element.defaultStyles || {};
  
  const path = element.stylePath;
  const parts = path.split('.');
  let value = blockProps;
  
  for (const part of parts) {
    if (value === undefined || value === null) {
      return element.defaultStyles || {};
    }
    value = value[part];
  }
  
  return { ...(element.defaultStyles || {}), ...(value || {}) };
};

/**
 * Set styles in block props using stylePath
 */
export const setElementStyles = (blockProps, element, newStyles) => {
  if (!element.stylePath) return blockProps;
  
  const path = element.stylePath;
  const parts = path.split('.');
  const result = { ...blockProps };
  
  // Ensure styles object exists
  if (!result.styles) result.styles = {};
  
  let current = result;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    current[part] = current[part] ? { ...current[part] } : {};
    current = current[part];
  }
  
  current[parts[parts.length - 1]] = newStyles;
  return result;
};

/**
 * Get motion from block props using motionPath
 */
export const getElementMotion = (blockProps, element) => {
  if (!element.motionPath) return element.defaultMotion || DEFAULT_MOTION;
  
  const path = element.motionPath;
  const parts = path.split('.');
  let value = blockProps;
  
  for (const part of parts) {
    if (value === undefined || value === null) {
      return element.defaultMotion || DEFAULT_MOTION;
    }
    value = value[part];
  }
  
  return { ...(element.defaultMotion || DEFAULT_MOTION), ...(value || {}) };
};

/**
 * Set motion in block props using motionPath
 */
export const setElementMotion = (blockProps, element, newMotion) => {
  if (!element.motionPath) return blockProps;
  
  const path = element.motionPath;
  const parts = path.split('.');
  const result = { ...blockProps };
  
  // Ensure motion object exists
  if (!result.motion) result.motion = {};
  
  let current = result;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    current[part] = current[part] ? { ...current[part] } : {};
    current = current[part];
  }
  
  current[parts[parts.length - 1]] = newMotion;
  return result;
};

export default {
  ELEMENT_TYPES,
  STYLE_PRESETS,
  MOTION_PRESETS,
  DEFAULT_STYLES,
  DEFAULT_MOTION,
  BLOCK_ELEMENTS_REGISTRY,
  getBlockElements,
  getElementById,
  getElementValue,
  setElementValue,
  getElementStyles,
  setElementStyles,
  getElementMotion,
  setElementMotion,
};
